import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/EmployeeDashboard.css";
import NotificationBell from "../components/NotificationBell";
import { apiUrl } from "../lib/api";
const fallbackProductImage =
  "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=900&q=80";

function EmployeeIcon({ name, className = "" }) {
  const icons = {
    menu: <path d="M4 7h16M4 12h16M4 17h16" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />,
    home: <path d="M5.5 10.2 12 5l6.5 5.2V18H14v-4h-4v4H5.5v-7.8Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.8" />,
    inventory: (
      <>
        <path d="M7 6.2h10l1.3 3.1H5.7L7 6.2Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.7" />
        <path d="M6.5 9.3h11v8.7H6.5z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.7" />
      </>
    ),
    tasks: (
      <>
        <rect x="6" y="4.5" width="12" height="15" rx="2.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="M9 9.2h6M9 12.5h6M9 15.8h4" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
      </>
    ),
    schedule: (
      <>
        <rect x="5" y="6.2" width="14" height="12.8" rx="2.4" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 4.7v3M16 4.7v3M8.5 11.2h7" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
      </>
    ),
    profile: (
      <>
        <circle cx="12" cy="9" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="M5.5 18c1.8-2.7 4.2-4 6.5-4s4.7 1.3 6.5 4" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
      </>
    ),
    check: <path d="m6.5 12.2 3.3 3.3 7.7-7.7" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />,
    note: (
      <>
        <path d="M6 5.5h12v13H6z" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8.5 9.3h7M8.5 12.5h7" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
      </>
    ),
    search: (
      <>
        <circle cx="10.5" cy="10.5" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path d="m14 14 4 4" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 8.5v4.2l2.8 1.8" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
      </>
    ),
  };

  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      {icons[name] || icons.home}
    </svg>
  );
}

const formatShiftDate = (value) =>
  new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
  });

const employeeSectionTitles = {
  overview: "Main Dashboard",
  inventory: "Department Inventory",
  tasks: "Assigned Tasks",
  schedule: "Shift Schedule",
  profile: "Employee Profile",
};

function EmployeeDashboard({ onLogout }) {
  const [username] = useState(localStorage.getItem("username") || "Employee");
  const [activeSection, setActiveSection] = useState("overview");
  const [tasks, setTasks] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [changeRequests, setChangeRequests] = useState({});
  const [inventorySearch, setInventorySearch] = useState("");

  const storedEmployeeId = localStorage.getItem("userId");
  const employeeRole = localStorage.getItem("role") || "employee";
  const employeeEmail = localStorage.getItem("email") || "No email saved";
  const employeePhone = localStorage.getItem("phone") || "No phone saved";
  const employeeAddress = localStorage.getItem("address") || "No address saved";
  const navigate = useNavigate();

  const fetchInventory = useCallback(async () => {
    try {
      const response = await axios.get(apiUrl("/api/inventory"));
      setInventory(response.data || []);
    } catch (error) {
      console.error("Error fetching inventory:", error.response?.data || error.message);
      setInventory([]);
    }
  }, []);

  const fetchEmployeeData = useCallback(async () => {
    if (!storedEmployeeId) {
      setTasks([]);
      setSchedule([]);
      return;
    }

    try {
      const [taskResponse, scheduleResponse] = await Promise.all([
        axios.get(apiUrl(`/api/tasks/${storedEmployeeId}`)),
        axios.get(apiUrl(`/api/schedule/${storedEmployeeId}`)),
      ]);

      setTasks(taskResponse.data || []);
      setSchedule(scheduleResponse.data || []);
    } catch (error) {
      console.error("Error fetching employee data:", error.response?.data || error.message || error);
    }
  }, [storedEmployeeId]);

  useEffect(() => {
    fetchInventory();
    fetchEmployeeData();
  }, [fetchInventory, fetchEmployeeData]);

  const pendingTasks = useMemo(() => tasks.filter((task) => task.status !== "Completed"), [tasks]);
  const completedTasks = useMemo(() => tasks.filter((task) => task.status === "Completed"), [tasks]);
  const shifts = useMemo(() => schedule[0]?.shifts || [], [schedule]);
  const lowStockItems = useMemo(
    () => inventory.filter((item) => Number(item.stockLevel || 0) < 20),
    [inventory],
  );
  const filteredInventory = useMemo(() => {
    const query = inventorySearch.trim().toLowerCase();
    if (!query) {
      return inventory;
    }

    return inventory.filter((item) =>
      [item.name, item.category, item.department]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query)),
    );
  }, [inventory, inventorySearch]);

  const nextShift = shifts[0] || null;
  const firstName = username.split(" ")[0] || "Alex";
  const activeSectionTitle = employeeSectionTitles[activeSection] || "Main Dashboard";

  const handleImageFallback = (event) => {
    if (event.currentTarget.src !== fallbackProductImage) {
      event.currentTarget.src = fallbackProductImage;
    }
  };

  const markAsCompleted = async (taskId) => {
    try {
      await axios.patch(apiUrl(`/api/tasks/${taskId}`), { status: "Completed" });
      setTasks((current) =>
        current.map((task) => (task._id === taskId ? { ...task, status: "Completed" } : task)),
      );
    } catch (error) {
      console.error("Error updating task:", error.response?.data || error);
    }
  };

  const handleRequestChange = async (itemId) => {
    const message = (changeRequests[itemId] || "").trim();
    if (!message) {
      return;
    }

    try {
      await axios.post(apiUrl("/api/inventory/request-change"), {
        itemId,
        message,
        employee: username,
      });
      setChangeRequests((current) => ({ ...current, [itemId]: "" }));
    } catch (error) {
      console.error("Error submitting request:", error.response?.data || error);
    }
  };

  const logout = () => {
    if (typeof onLogout === "function") {
      onLogout();
      return;
    }

    ["token", "userId", "employeeId", "username", "email", "phone", "address", "role"].forEach((key) =>
      localStorage.removeItem(key),
    );
    navigate("/");
  };

  const renderOverview = () => (
    <section className="employee-screen">
      <section className="employee-greeting">
        <p>Good Morning, {firstName}</p>
        <h1>Main Dashboard</h1>
      </section>

      <section className="employee-overview-grid">
        <article className="employee-card employee-card--status">
          <div className="employee-card__header">
            <div>
              <span className="employee-label">On shift</span>
              <h2>Today&apos;s focus</h2>
            </div>
            <span className="employee-pill employee-pill--live">{nextShift?.notes || "Aisle 3 Manager"}</span>
          </div>

          <div className="employee-shift-summary">
            <div className="employee-shift-summary__icon">
              <EmployeeIcon name="clock" />
            </div>
            <div className="employee-shift-summary__copy">
              <strong>{nextShift ? formatShiftDate(nextShift.shiftStart) : "08:00 - 16:00"}</strong>
              <p>{nextShift ? nextShift.notes || "Floor support and stock checks." : "Next break: 12:30 PM"}</p>
            </div>
          </div>

          <section className="employee-mini-stats">
            <article className="employee-mini-card employee-mini-card--tasks">
              <div className="employee-mini-card__icon">
                <EmployeeIcon name="tasks" />
              </div>
              <span>Open tasks</span>
              <strong>{pendingTasks.length}</strong>
            </article>
            <article className="employee-mini-card employee-mini-card--note">
              <div className="employee-mini-card__icon">
                <EmployeeIcon name="note" />
              </div>
              <span>Manager note</span>
              <strong>{nextShift?.notes ? 1 : 0}</strong>
            </article>
          </section>
        </article>

        <article className="employee-card employee-card--profile-summary">
          <div className="employee-card__header">
            <div>
              <span className="employee-label">Employee Profile</span>
              <h2>Your details</h2>
            </div>
            <button type="button" className="employee-link" onClick={() => setActiveSection("profile")}>
              Open profile
            </button>
          </div>

          <div className="employee-profile-summary">
            <div className="employee-profile-summary__badge">{username?.charAt(0) || "E"}</div>
            <div>
              <strong>{username}</strong>
              <p>{employeeRole}</p>
            </div>
          </div>

          <div className="employee-profile-list">
            <article>
              <span>Employee ID</span>
              <strong>{storedEmployeeId || "Unavailable"}</strong>
            </article>
            <article>
              <span>Email</span>
              <strong>{employeeEmail}</strong>
            </article>
          </div>
        </article>
      </section>

      <section className="employee-detail-grid">
        <article className="employee-card">
          <div className="employee-card__header">
            <div>
              <span className="employee-label">Assigned Tasks</span>
              <h2>Assigned tasks</h2>
            </div>
            <button type="button" className="employee-link" onClick={() => setActiveSection("tasks")}>
              View all
            </button>
          </div>

          {pendingTasks.length ? (
            <div className="employee-task-list">
              {pendingTasks.slice(0, 3).map((task) => (
                <article key={task._id} className="employee-task-card">
                  <div className="employee-task-card__check">
                    <EmployeeIcon name={task.status === "Completed" ? "check" : "tasks"} />
                  </div>
                  <div className="employee-task-card__copy">
                    <strong>{task.title}</strong>
                    <p>{task.description}</p>
                  </div>
                  <button type="button" className="employee-primary-button" onClick={() => markAsCompleted(task._id)}>
                    Complete
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className="employee-empty-state">
              <strong>No urgent tasks.</strong>
              <p>Your next assigned work will appear here.</p>
            </div>
          )}
        </article>

        <article className="employee-card">
          <div className="employee-card__header">
            <div>
              <span className="employee-label">Department Inventory</span>
              <h2>Department inventory</h2>
            </div>
            <button type="button" className="employee-link" onClick={() => setActiveSection("inventory")}>
              Open inventory
            </button>
          </div>

          <div className="employee-quick-inventory">
            {(lowStockItems.length ? lowStockItems : inventory).slice(0, 3).map((item) => (
              <article key={item._id} className="employee-stock-row">
                <img
                  className="employee-stock-row__image"
                  src={item.image || fallbackProductImage}
                  alt={item.name}
                  onError={handleImageFallback}
                />
                <div>
                  <strong>{item.name}</strong>
                  <p>{item.category || "Store item"}</p>
                </div>
                <div className="employee-stock-row__meta">
                  <strong>{item.stockLevel}</strong>
                  <span>{Number(item.stockLevel || 0) < 20 ? "Low stock" : "Healthy"}</span>
                </div>
              </article>
            ))}
          </div>
        </article>
      </section>

      <section className="employee-detail-grid">
        <article className="employee-card">
          <div className="employee-card__header">
            <div>
              <span className="employee-label">Upcoming Schedule</span>
              <h2>Upcoming schedule</h2>
            </div>
          </div>

          {shifts.length ? (
            <div className="employee-schedule-strip">
              {shifts.slice(0, 5).map((shift, index) => (
                <article key={`${shift.shiftStart}-${index}`} className="employee-schedule-chip">
                  <span>
                    {new Date(shift.shiftStart).toLocaleDateString("en-US", {
                      weekday: "short",
                      timeZone: "America/New_York",
                    })}
                  </span>
                  <strong>
                    {new Date(shift.shiftStart).toLocaleDateString("en-US", {
                      day: "numeric",
                      timeZone: "America/New_York",
                    })}
                  </strong>
                  <small>
                    {new Date(shift.shiftStart).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                      timeZone: "America/New_York",
                    })}
                  </small>
                </article>
              ))}
            </div>
          ) : (
            <div className="employee-empty-state">
              <strong>No scheduled shifts yet.</strong>
              <p>Assigned shifts will show here when available.</p>
            </div>
          )}
        </article>

        <section className="employee-action-grid">
          <button type="button" className="employee-action-card" onClick={() => setActiveSection("inventory")}>
            <span>Report Stock Issue</span>
            <strong>Flag missing tags, low stock, or damaged items</strong>
          </button>
          <button
            type="button"
            className="employee-action-card employee-action-card--primary"
            onClick={() => setActiveSection("schedule")}
          >
            <span>Open Schedule</span>
            <strong>Review upcoming shifts and manager notes</strong>
          </button>
        </section>
      </section>
    </section>
  );

  const renderTasks = () => (
    <section className="employee-screen">
      <article className="employee-card">
        <div className="employee-card__header">
          <div>
            <span className="employee-label">Assigned Tasks</span>
            <h2>Your task list</h2>
          </div>
        </div>

        {tasks.length ? (
          <div className="employee-task-list employee-task-list--full">
            {tasks.map((task) => (
              <article key={task._id} className="employee-task-card employee-task-card--full">
                <div>
                  <strong>{task.title}</strong>
                  <p>{task.description}</p>
                  <span className={`employee-pill ${task.status === "Completed" ? "employee-pill--done" : ""}`}>
                    {task.status}
                  </span>
                </div>
                {task.status !== "Completed" ? (
                  <button type="button" className="employee-primary-button" onClick={() => markAsCompleted(task._id)}>
                    Mark completed
                  </button>
                ) : (
                  <span className="employee-task-done">Completed</span>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="employee-empty-state">
            <strong>No tasks assigned.</strong>
            <p>New work will show up here when the manager adds it.</p>
          </div>
        )}
      </article>
    </section>
  );

  const renderInventory = () => (
    <section className="employee-screen">
      <article className="employee-card">
        <div className="employee-card__header">
          <div>
            <span className="employee-label">Inventory Search</span>
            <h2>Department inventory</h2>
          </div>
        </div>

        <div className="employee-search">
          <span className="employee-search__icon">
            <EmployeeIcon name="search" />
          </span>
          <input
            type="text"
            placeholder="Search stock like milk, apples, bread..."
            value={inventorySearch}
            onChange={(event) => setInventorySearch(event.target.value)}
          />
        </div>

        {filteredInventory.length ? (
          <div className="employee-inventory-list">
            {filteredInventory.map((item) => (
              <article key={item._id} className="employee-inventory-item">
                <div className="employee-inventory-item__header">
                  <img
                    className="employee-inventory-item__image"
                    src={item.image || fallbackProductImage}
                    alt={item.name}
                    onError={handleImageFallback}
                  />
                  <div className="employee-inventory-item__copy">
                    <strong>{item.name}</strong>
                    <p>{item.category || "Store item"}</p>
                  </div>
                  <div className="employee-inventory-item__stock">
                    <strong>{item.stockLevel} left</strong>
                    <span>{Number(item.stockLevel || 0) < 20 ? "Low stock" : "Healthy"}</span>
                  </div>
                </div>

                <div className="employee-inventory-item__request">
                  <textarea
                    placeholder="Describe the update you need for this item"
                    value={changeRequests[item._id] || ""}
                    onChange={(event) =>
                      setChangeRequests((current) => ({ ...current, [item._id]: event.target.value }))
                    }
                  />
                  <button type="button" className="employee-primary-button" onClick={() => handleRequestChange(item._id)}>
                    Request change
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="employee-empty-state">
            <strong>No products match that search.</strong>
            <p>Try a different item name or category.</p>
          </div>
        )}
      </article>
    </section>
  );

  const renderSchedule = () => (
    <section className="employee-screen">
      <article className="employee-card">
        <div className="employee-card__header">
          <div>
            <span className="employee-label">Upcoming Schedule</span>
            <h2>Your assigned shifts</h2>
          </div>
        </div>

        {shifts.length ? (
          <div className="employee-schedule-list">
            {shifts.map((shift, index) => (
              <article key={`${shift.shiftStart}-${index}`} className="employee-schedule-row">
                <div>
                  <strong>{formatShiftDate(shift.shiftStart)}</strong>
                  <p>Ends {formatShiftDate(shift.shiftEnd)}</p>
                </div>
                <span>{shift.notes || "Regular shift coverage"}</span>
              </article>
            ))}
          </div>
        ) : (
          <div className="employee-empty-state">
            <strong>No schedule assigned yet.</strong>
            <p>Check back after your manager adds your next shift.</p>
          </div>
        )}
      </article>
    </section>
  );

  const renderProfile = () => (
    <section className="employee-screen">
      <article className="employee-card">
        <div className="employee-card__header">
          <div>
            <span className="employee-label">Employee Profile</span>
            <h2>Your information</h2>
          </div>
        </div>

        <div className="employee-profile-hero">
          <div className="employee-profile-hero__avatar">{username?.charAt(0) || "E"}</div>
          <div>
            <strong>{username}</strong>
            <p>{employeeRole}</p>
          </div>
        </div>

        <div className="employee-profile-grid">
          <article>
            <span>Employee ID</span>
            <strong>{storedEmployeeId || "Unavailable"}</strong>
          </article>
          <article>
            <span>Role</span>
            <strong>{employeeRole}</strong>
          </article>
          <article>
            <span>Email</span>
            <strong>{employeeEmail}</strong>
          </article>
          <article>
            <span>Phone</span>
            <strong>{employeePhone}</strong>
          </article>
          <article className="employee-profile-grid__wide">
            <span>Address</span>
            <strong>{employeeAddress}</strong>
          </article>
        </div>

        <div className="employee-profile-notes">
          <article>
            <span>Shifts assigned</span>
            <strong>{shifts.length}</strong>
          </article>
          <article>
            <span>Tasks completed</span>
            <strong>{completedTasks.length}</strong>
          </article>
          <article>
            <span>Open items</span>
            <strong>{pendingTasks.length}</strong>
          </article>
        </div>

        <button type="button" className="employee-logout-button employee-logout-button--profile" onClick={logout}>
          Logout
        </button>
      </article>
    </section>
  );

  return (
    <div className="employee-dashboard">
      <header className="employee-topbar">
        <div className="employee-topbar__brand">
          <button type="button" className="employee-icon-button" aria-label="Open menu">
            <EmployeeIcon name="menu" />
          </button>
          <div className="employee-topbar__badge">IG</div>
          <div>
            <span>IntelliGrocer</span>
            <strong>{activeSectionTitle}</strong>
          </div>
        </div>

        <div className="employee-topbar__actions">
          <NotificationBell userId={storedEmployeeId} />
          <button type="button" className="employee-logout-button employee-logout-button--topbar" onClick={logout}>
            Logout
          </button>
          <button type="button" className="employee-avatar-button" onClick={() => setActiveSection("profile")}>
            {(username || "E").charAt(0).toUpperCase()}
          </button>
        </div>
      </header>

      <main className="employee-main">
        <section className="employee-top-tabs">
          {[
            ["overview", "Overview"],
            ["inventory", "Inventory"],
            ["tasks", "Tasks"],
            ["schedule", "Schedule"],
            ["profile", "Profile"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={activeSection === key ? "is-active" : ""}
              onClick={() => setActiveSection(key)}
          >
              <EmployeeIcon
                name={
                  key === "overview"
                    ? "home"
                    : key === "inventory"
                      ? "inventory"
                      : key === "tasks"
                        ? "tasks"
                        : key === "schedule"
                          ? "schedule"
                          : "profile"
                }
              />
              <span>{label}</span>
            </button>
          ))}
        </section>

        {activeSection === "overview" && renderOverview()}
        {activeSection === "tasks" && renderTasks()}
        {activeSection === "inventory" && renderInventory()}
        {activeSection === "schedule" && renderSchedule()}
        {activeSection === "profile" && renderProfile()}
      </main>

      <nav className="employee-bottom-nav">
        {[
          ["overview", "Home"],
          ["inventory", "Inventory"],
          ["tasks", "Tasks"],
          ["schedule", "Schedule"],
          ["profile", "Profile"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={activeSection === key ? "is-active" : ""}
            onClick={() => {
              setActiveSection(key);
            }}
          >
            <EmployeeIcon
              name={
                key === "overview"
                  ? "home"
                  : key === "inventory"
                    ? "inventory"
                    : key === "tasks"
                      ? "tasks"
                      : key === "schedule"
                        ? "schedule"
                        : key === "profile"
                          ? "profile"
                          : "profile"
              }
            />
            <span>{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default EmployeeDashboard;
